import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import { useQuery } from '@tanstack/react-query'

import { listTags } from 'api/tags'
import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Input } from 'components/Input'
import { useUnsavedChangesGuard } from 'components/OrganizationScope/context'
import { TagSelector } from 'components/TagSelector'
import { Textarea } from 'components/Textarea'
import { queryKeys } from 'constants/queryKeys'
import { adCodeStrings, commonStrings } from 'constants/strings'
import { useAuth } from 'contexts/AuthContext'
import { useAdCodeMedia } from 'hooks/useAdCodes'
import { useCurrentUser } from 'hooks/useCurrentUser'
import type { TAdCodeDetail, TAdCodeMedium, TUpsertAdCodeRequest } from 'types'
import { EOrganizationFeature } from 'types/enum'
import {
  canFetchOrganizationFeature,
  isOrganizationFeatureEnabled,
} from 'utils/organizationFeature'
import {
  AD_CODE_MEDIUM_MAX_LENGTH,
  buildMediumOptions,
  CUSTOM_MEDIUM,
  isPresetMedium,
  normalizeMedium,
} from '../adCodeDisplay'

type Props = {
  initial?: TAdCodeDetail
  saving: boolean
  error?: string
  submitLabel: string
  onSubmit: (request: TUpsertAdCodeRequest) => void
  onCancel: () => void
}

/**
 * 広告コードの作成・編集フォーム。
 *
 * コード文字列はサーバーが発行するのでここでは扱わない（作成後に詳細画面で見せる）。
 */
export function AdCodeForm({
  initial,
  saving,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: Props) {
  const { user } = useAuth()
  const { data: currentUser } = useCurrentUser()
  const canUseTags = isOrganizationFeatureEnabled(
    currentUser,
    EOrganizationFeature.TAG_MANAGEMENT,
  )
  const fieldId = useId()
  const mediaQuery = useAdCodeMedia()
  const [name, setName] = useState('')
  const [medium, setMedium] = useState<TAdCodeMedium>('OTHER')
  const [customMedium, setCustomMedium] = useState('')
  const [memo, setMemo] = useState('')
  const [tagIds, setTagIds] = useState<number[]>([])
  const [applyTagsOnEstimated, setApplyTagsOnEstimated] = useState(false)
  const [validationErrors, setValidationErrors] = useState<{
    name?: string
    medium?: string
  }>({})
  const markSaved = useUnsavedChangesGuard(
    JSON.stringify({
      name,
      medium,
      customMedium,
      memo,
      tagIds,
      applyTagsOnEstimated,
    }),
  )
  // 取得待ち・失敗時も編集中の媒体を選択肢から落とさない。
  // 同一媒体の別表記が取得されても、選択中の値と一致する候補を優先する。
  const options = buildMediumOptions([
    medium,
    initial?.medium ?? '',
    ...mediaQuery.media,
  ])
  // 初期値の流し込みは対象が変わったときだけ。参照の変化（再フェッチ）で
  // 走らせると、編集中の入力が保存前に巻き戻る。
  const initialRef = useRef(initial)
  initialRef.current = initial
  const initialId = initial?.id
  useEffect(() => {
    const values = initialRef.current
    if (!values) return
    setName(values.name)
    const initialMedium = normalizeMedium(values.medium)
    setMedium(initialMedium)
    setCustomMedium(isPresetMedium(initialMedium) ? '' : initialMedium)
    setMemo(values.memo ?? '')
    setTagIds(values.tags.map(({ id }) => id))
    setApplyTagsOnEstimated(values.applyTagsOnEstimated)
    setValidationErrors({})
    markSaved()
  }, [initialId, markSaved])

  const tagsQuery = useQuery({
    queryKey: [queryKeys.TAGS, user?.uid],
    // タグ管理がOFFの組織ではタグAPIが403になるので取得しない
    enabled:
      Boolean(user?.uid) &&
      canFetchOrganizationFeature(currentUser, EOrganizationFeature.TAG_MANAGEMENT),
    queryFn: listTags,
    retry: false,
  })

  const mediumError = (value: string) => {
    if (!value.trim()) return adCodeStrings.errors.mediumRequired
    if (value.trim().length > AD_CODE_MEDIUM_MAX_LENGTH) {
      return adCodeStrings.errors.mediumTooLong
    }
    return undefined
  }

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (saving) return
    const trimmed = name.trim()
    const submittedMedium = medium === CUSTOM_MEDIUM ? customMedium : medium
    const errors = {
      name: trimmed ? undefined : adCodeStrings.errors.nameRequired,
      medium: mediumError(submittedMedium),
    }
    setValidationErrors(errors)
    if (errors.name || errors.medium) {
      const field = event.currentTarget.elements.namedItem(
        errors.name ? 'name' : 'customMedium',
      )
      if (field instanceof HTMLElement) field.focus()
      return
    }
    onSubmit({
      name: trimmed,
      medium: normalizeMedium(submittedMedium),
      memo: memo.trim() || null,
      // タグ管理がOFFの組織では tagIds を送らない（送ると既存タグが全解除される）
      ...(canUseTags ? { tagIds } : {}),
      applyTagsOnEstimated,
    })
  }

  const toggle = (list: number[], id: number) =>
    list.includes(id) ? list.filter((item) => item !== id) : [...list, id]

  return (
    <form
      className="grid min-w-0 max-w-2xl gap-5"
      onSubmit={handleSubmit}
      noValidate
    >
      {error && <Alert>{error}</Alert>}

      <div className="grid gap-2">
        <label
          htmlFor={`${fieldId}-name`}
          className="text-xs font-semibold text-ink"
        >
          {adCodeStrings.form.name}
        </label>
        <Input
          id={`${fieldId}-name`}
          name="name"
          required
          value={name}
          maxLength={50}
          aria-invalid={Boolean(validationErrors.name)}
          aria-describedby={`${fieldId}-name-help${validationErrors.name ? ` ${fieldId}-name-error` : ''}`}
          onChange={(event) => {
            setName(event.target.value)
            if (event.target.value.trim()) {
              setValidationErrors((previous) => ({
                ...previous,
                name: undefined,
              }))
            }
          }}
        />
        <span id={`${fieldId}-name-help`} className="text-[11px] text-muted">
          {adCodeStrings.form.nameHelp}
        </span>
        {validationErrors.name && (
          <p
            id={`${fieldId}-name-error`}
            role="alert"
            className="text-xs text-danger"
          >
            {validationErrors.name}
          </p>
        )}
      </div>

      <label className="grid min-w-0 gap-2">
        <span className="text-xs font-semibold text-ink">
          {adCodeStrings.form.medium}
        </span>
        <select
          className="h-11 min-w-0 w-full rounded-2xl border border-border/80 bg-white/80 px-3 text-sm text-ink"
          value={medium}
          onChange={(event) => {
            setMedium(event.target.value)
            setValidationErrors((previous) => ({
              ...previous,
              medium: undefined,
            }))
          }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
          <option value={CUSTOM_MEDIUM}>
            {adCodeStrings.form.customMediumOption}
          </option>
        </select>
        <span className="text-[11px] text-muted">
          {adCodeStrings.form.mediumHelp}
        </span>
      </label>

      {mediaQuery.loading && (
        <p className="text-xs text-muted">{commonStrings.loading}</p>
      )}
      {mediaQuery.error && (
        <Alert>
          {mediaQuery.error}
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="ml-2"
            onClick={() => void mediaQuery.refetch()}
            disabled={mediaQuery.loading}
          >
            {commonStrings.actions.retry}
          </Button>
        </Alert>
      )}

      {medium === CUSTOM_MEDIUM && (
        <div className="grid gap-2">
          <label
            htmlFor={`${fieldId}-medium`}
            className="text-xs font-semibold text-ink"
          >
            {adCodeStrings.form.customMedium}
          </label>
          <Input
            id={`${fieldId}-medium`}
            name="customMedium"
            required
            value={customMedium}
            maxLength={AD_CODE_MEDIUM_MAX_LENGTH}
            placeholder={adCodeStrings.form.customMediumPlaceholder}
            aria-invalid={Boolean(validationErrors.medium)}
            aria-describedby={`${fieldId}-medium-help${validationErrors.medium ? ` ${fieldId}-medium-error` : ''}`}
            onChange={(event) => {
              setCustomMedium(event.target.value)
              if (validationErrors.medium) {
                setValidationErrors((previous) => ({
                  ...previous,
                  medium: mediumError(event.target.value),
                }))
              }
            }}
          />
          <span
            id={`${fieldId}-medium-help`}
            className="text-[11px] text-muted"
          >
            {adCodeStrings.form.customMediumHelp}
          </span>
          {validationErrors.medium && (
            <p
              id={`${fieldId}-medium-error`}
              role="alert"
              className="text-xs text-danger"
            >
              {validationErrors.medium}
            </p>
          )}
        </div>
      )}

      <label className="grid gap-2">
        <span className="text-xs font-semibold text-ink">
          {adCodeStrings.form.memo}
        </span>
        <Textarea
          value={memo}
          rows={2}
          maxLength={500}
          placeholder={adCodeStrings.form.memoPlaceholder}
          onChange={(event) => setMemo(event.target.value)}
        />
      </label>

      {canUseTags && (
        <div className="grid gap-2">
          <span className="text-xs font-semibold text-ink">
            {adCodeStrings.form.tags}
          </span>
          <TagSelector
            tags={tagsQuery.data ?? []}
            selectedTagIds={tagIds}
            onToggle={(tagId) => setTagIds((prev) => toggle(prev, tagId))}
          />
          <span className="text-[11px] text-muted">
            {adCodeStrings.form.tagsHelp}
          </span>
        </div>
      )}

      {canUseTags && (
        <div className="grid gap-1">
          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={applyTagsOnEstimated}
              onChange={(event) => setApplyTagsOnEstimated(event.target.checked)}
            />
            {adCodeStrings.form.applyTagsOnEstimated}
          </label>
          <span className="text-[11px] text-muted">
            {adCodeStrings.form.applyTagsOnEstimatedHelp}
          </span>
        </div>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          {submitLabel}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={saving}
        >
          {adCodeStrings.form.cancel}
        </Button>
      </div>
    </form>
  )
}
